import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { TranscriptionMethod } from "@/config/transcriptionStrategy";
import type { AudioAnalysisSummary, UploadStage } from "@/hooks/useAudioTranscriptPipeline";

export type UploadFailureContext = "extract" | "upload" | "transcribe" | "process";
export const MANUAL_MEDIA_UPLOAD_MAX_BYTES = 500 * 1024 * 1024;
export const OPENAI_TRANSCRIPTION_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const GOOGLE_SYNCHRONOUS_RECOGNITION_MAX_BYTES = 10 * 1024 * 1024;

export function weightedProgress(min: number, max: number, progress: number) {
  const clamped = Math.max(0, Math.min(100, progress));
  return Math.round(min + ((max - min) * clamped) / 100);
}

export function resetPipelineState({
  setStage,
  setErrorMessage,
  setAnalysis,
  setExtractionProgress,
  setUploadProgress,
  setTranscriptionProgress,
  setTranscriptionMethod,
  setTranscriptReady,
  transcriptionMethodRef,
  transcriptionFailureMessageRef,
}: {
  setStage: Dispatch<SetStateAction<UploadStage>>;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  setAnalysis: Dispatch<SetStateAction<AudioAnalysisSummary | null>>;
  setExtractionProgress: Dispatch<SetStateAction<number>>;
  setUploadProgress: Dispatch<SetStateAction<number>>;
  setTranscriptionProgress: Dispatch<SetStateAction<number>>;
  setTranscriptionMethod: Dispatch<SetStateAction<TranscriptionMethod | null>>;
  setTranscriptReady: Dispatch<SetStateAction<boolean>>;
  transcriptionMethodRef: MutableRefObject<TranscriptionMethod | null>;
  transcriptionFailureMessageRef: MutableRefObject<string | null>;
}) {
  setStage("idle");
  setErrorMessage(null);
  setAnalysis(null);
  setExtractionProgress(0);
  setUploadProgress(0);
  setTranscriptionProgress(0);
  setTranscriptionMethod(null);
  transcriptionMethodRef.current = null;
  transcriptionFailureMessageRef.current = null;
  setTranscriptReady(false);
}

export function initializeUploadRun({
  fileName,
  setFileName,
  setStage,
  setErrorMessage,
  setAnalysis,
  setExtractionProgress,
  setUploadProgress,
  setTranscriptionProgress,
  setTranscriptionMethod,
  setTranscriptReady,
  transcriptionMethodRef,
  transcriptionFailureMessageRef,
}: {
  fileName: string;
  setFileName: Dispatch<SetStateAction<string | null>>;
  setStage: Dispatch<SetStateAction<UploadStage>>;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  setAnalysis: Dispatch<SetStateAction<AudioAnalysisSummary | null>>;
  setExtractionProgress: Dispatch<SetStateAction<number>>;
  setUploadProgress: Dispatch<SetStateAction<number>>;
  setTranscriptionProgress: Dispatch<SetStateAction<number>>;
  setTranscriptionMethod: Dispatch<SetStateAction<TranscriptionMethod | null>>;
  setTranscriptReady: Dispatch<SetStateAction<boolean>>;
  transcriptionMethodRef: MutableRefObject<TranscriptionMethod | null>;
  transcriptionFailureMessageRef: MutableRefObject<string | null>;
}) {
  setFileName(fileName);
  resetPipelineState({
    setStage,
    setErrorMessage,
    setAnalysis,
    setExtractionProgress,
    setUploadProgress,
    setTranscriptionProgress,
    setTranscriptionMethod,
    setTranscriptReady,
    transcriptionMethodRef,
    transcriptionFailureMessageRef,
  });
  setStage("extracting");
}

export function buildAudioUploadArtifacts({
  fileName,
  audioBlob,
  audioFormat,
  leaderId,
  meetingId,
  uuid,
}: {
  fileName: string;
  audioBlob: Blob;
  audioFormat: string;
  leaderId: string;
  meetingId?: string;
  uuid: string;
}) {
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]+/g, "_");
  const pathMeetingId = meetingId ?? "unassigned";
  const audioPath = `leader/${leaderId}/audio_tmp/${pathMeetingId}/${uuid}-${sanitizedFileName}.${audioFormat}`;
  const audioFile = new File([audioBlob], `${sanitizedFileName}.${audioFormat}`, {
    type: normalizeAudioMimeType(audioBlob.type, audioFormat),
  });

  return {
    audioPath,
    audioFile,
  };
}

export function deriveRecordedAudioFormat(audioBlob: Blob): "webm" | "m4a" {
  const mimeType = audioBlob.type.toLowerCase();
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4") || mimeType.includes("m4a") || mimeType.includes("aac")) return "m4a";
  return "webm";
}

function normalizeAudioMimeType(mimeType: string, audioFormat: string) {
  if (audioFormat === "webm" && mimeType.startsWith("audio/webm")) {
    return "audio/webm";
  }

  if (
    audioFormat === "m4a" &&
    (
      mimeType === "" ||
      mimeType === "audio/x-m4a" ||
      mimeType === "audio/m4a" ||
      mimeType.startsWith("audio/mp4") ||
      mimeType.startsWith("audio/aac")
    )
  ) {
    return "audio/mp4";
  }

  return mimeType;
}

export function buildVideoUploadArtifacts({
  fileName,
  videoFile,
  leaderId,
  meetingId,
  uuid,
}: {
  fileName: string;
  videoFile: File;
  leaderId: string;
  meetingId?: string;
  uuid: string;
}) {
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]+/g, "_");
  const pathMeetingId = meetingId ?? "unassigned";
  const videoPath = `leader/${leaderId}/video_jobs/${pathMeetingId}/${uuid}-${sanitizedFileName}`;

  return {
    videoPath,
    videoFile: new File([videoFile], sanitizedFileName, { type: videoFile.type }),
  };
}

export function shouldExtractAudio(file: File) {
  return !file.type.startsWith("audio/");
}

export function shouldQueueVideoForBackgroundProcessing(file: File) {
  if (file.type.startsWith("audio/")) {
    return false;
  }

  if (file.type === "video/mp4" || file.type === "video/webm") {
    return true;
  }

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return extension === "mp4" || extension === "webm";
}

export async function uploadWithProgress({
  bucket,
  path,
  file,
  onProgress,
  allowDirectFallback = true,
}: {
  bucket: string;
  path: string;
  file: File;
  onProgress: (progress: number) => void;
  allowDirectFallback?: boolean;
}) {
  const { data: signedData, error: signedError } = await supabase.storage
    .from(bucket)
    .createSignedUploadUrl(path);

  if (signedData?.path && signedData.token) {
    try {
      onProgress(25);
      const { error } = await supabase.storage
        .from(bucket)
        .uploadToSignedUrl(signedData.path, signedData.token, file, {
          upsert: false,
          contentType: file.type,
        });

      if (error) throw error;
      onProgress(100);
      return;
    } catch (signedUploadError) {
      if (!allowDirectFallback) {
        throw signedUploadError;
      }
      console.warn("Signed upload failed, falling back to direct upload.", signedUploadError);
    }
  }

  if (signedError) {
    if (!allowDirectFallback) {
      throw signedError;
    }
    console.warn("Signed upload URL failed, falling back to direct upload.", signedError.message);
  }

  onProgress(0);
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: false,
    contentType: file.type,
  });

  if (error) throw error;
  onProgress(100);
}

export function buildUploadErrorMessage(
  error: unknown,
  context: UploadFailureContext,
  fileSizeBytes?: number,
  uploadKind: "audio" | "video" = "audio",
) {
  const specificMessage =
    error instanceof Error && error.message
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" && error !== null && "message" in error && typeof (error as { message?: unknown }).message === "string"
          ? (error as { message: string }).message
          : "";
  if (
    /maximum allowed size|exceeded the maximum allowed size/i.test(specificMessage) &&
    typeof fileSizeBytes === "number" &&
    fileSizeBytes > MANUAL_MEDIA_UPLOAD_MAX_BYTES
  ) {
    return "Please choose a recording under 500MB or compress the video and try again.";
  }
  if (
    /transcription file size limit/i.test(specificMessage) ||
    (
      typeof fileSizeBytes === "number" &&
      fileSizeBytes > OPENAI_TRANSCRIPTION_UPLOAD_MAX_BYTES &&
      context === "transcribe"
    )
  ) {
    return "Please choose an audio or video file under 25MB, or paste the transcript manually.";
  }
  if (context === "upload" && /signed upload failed|signed upload url failed/i.test(specificMessage)) {
    return uploadKind === "video"
      ? "We couldn't accept this recording upload. Please try again."
      : "We couldn't upload the extracted audio. Please try again.";
  }
  if (context === "upload" && /maximum allowed size|exceeded the maximum allowed size/i.test(specificMessage)) {
    return uploadKind === "video"
      ? "We couldn't accept this recording upload. Please try again."
      : "We couldn't upload the extracted audio. Please try again.";
  }
  const prefix = context === "extract"
    ? "We couldn't extract audio"
    : context === "upload"
      ? uploadKind === "video"
        ? "We couldn't accept this recording upload"
        : "We couldn't upload the extracted audio"
      : context === "transcribe"
        ? "We couldn't transcribe the audio"
        : "We couldn't process audio features";
  if (!error) {
    return `${prefix}. You can continue with transcript-only analysis.`;
  }
  if (specificMessage) {
    return `${prefix}: ${specificMessage}`;
  }
  return `${prefix}. You can continue with transcript-only analysis.`;
}

export function shouldFallbackVideoUploadToLocalAudio(error: unknown) {
  const specificMessage =
    error instanceof Error && error.message
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" && error !== null && "message" in error && typeof (error as { message?: unknown }).message === "string"
          ? (error as { message: string }).message
          : "";

  return /upload\/resumable/i.test(specificMessage)
    && /(413|maximum size exceeded)/i.test(specificMessage);
}

export function buildManualContinuationMessage(message: string) {
  const trimmed = message.trim();
  if (!trimmed) {
    return "We couldn't transcribe the audio. You can paste a transcript manually and continue.";
  }
  if (/paste a transcript manually and continue/i.test(trimmed)) {
    return trimmed;
  }
  return `We couldn't transcribe the audio: ${trimmed} You can paste a transcript manually and continue.`;
}
